import React from 'react'

export interface TooltipData {
  date: string
  metrics: Array<{
    label: string
    value: number | string
    color: string
    isComparison?: boolean
    normalizedPercent?: number
    scale?: { min: number; max: number }
  }>
}

interface ChartTooltipProps {
  data: TooltipData | null
  x: number
  y: number
  visible: boolean
}

export function ChartTooltip({ data, x, y, visible }: ChartTooltipProps) {
  if (!visible || !data) return null

  // Format values for display
  const formatValue = (value: number | string): string => {
    if (typeof value === 'string') return value

    // Try to format as currency if it looks like money
    if (value > 1000) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    }

    return value.toLocaleString('pt-BR', {
      maximumFractionDigits: 2
    })
  }

  // Color mapping for indicators
  const getColorClass = (color: string) => {
    const colorMap: { [key: string]: string } = {
      'brand-primary': 'bg-brand-primary',
      'brand-blue': 'bg-brand-blue',
      'success': 'bg-success',
      'warning': 'bg-warning',
      'error': 'bg-error'
    }
    return colorMap[color] || 'bg-brand-primary'
  }

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translate(-50%, -120%)'
      }}
    >
      <div className="bg-brand-dark-lighter border-2 border-brand-gray/30 rounded-monumenta shadow-2xl px-4 py-3 min-w-[200px]">
        {/* Date header */}
        <div className="text-brand-primary font-display font-semibold text-sm mb-2 pb-2 border-b border-brand-gray/20">
          {data.date}
        </div>

        {/* Metrics */}
        <div className="space-y-2.5">
          {data.metrics.map((metric, index) => (
            <div key={index} className="space-y-0.5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getColorClass(metric.color)} ${metric.isComparison ? 'opacity-50' : ''}`}></div>
                  <span className={`text-xs ${metric.isComparison ? 'text-brand-gray italic' : 'text-white'}`}>
                    {metric.label}
                    {metric.isComparison && ' (comp.)'}
                  </span>
                </div>
                <span className={`text-xs font-mono font-semibold ${metric.isComparison ? 'text-brand-gray' : 'text-brand-primary'}`}>
                  {formatValue(metric.value)}
                </span>
              </div>
              {/* Show normalized percentage and scale range */}
              {metric.normalizedPercent !== undefined && metric.scale && !metric.isComparison && (
                <div className="flex items-center justify-between gap-2 ml-4 text-[10px] text-brand-gray/70">
                  <span>{metric.normalizedPercent.toFixed(0)}% do alcance</span>
                  <span className="font-mono">
                    {formatValue(metric.scale.min)} - {formatValue(metric.scale.max)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Tooltip arrow */}
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0"
          style={{
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '8px solid rgba(131, 137, 152, 0.3)'
          }}
        ></div>
      </div>
    </div>
  )
}
